/* EZT support v1. Native Shopify POSTs; a private Worker handles photos only.
 * Never AJAX the contact POST or report success from a URL query parameter.
 * Photo uploads finish BEFORE the normal submit, preserving Shopify hCaptcha.
 */
(() => {
  'use strict';
  if (window.EZTSupport) { window.EZTSupport.init(); return; }
  const MAX_FILES = 3;
  const MAX_BYTES = 8 * 1024 * 1024;
  const DRAFT_AGE = 2 * 60 * 60 * 1000;
  const instances = new WeakMap();
  const by = (root, selector) => root.querySelector(selector);
  const all = (root, selector) => Array.from(root.querySelectorAll(selector));
  const storage = {
    get(key) { try { return JSON.parse(sessionStorage.getItem(key)); } catch (_) { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) {} },
    remove(key) { try { sessionStorage.removeItem(key); } catch (_) {} }
  };
  function id() {
    return window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
  function metric(event, root, extra = {}) {
    // No PII. No analytics until Shopify reports analytics consent is allowed.
    try {
      if (!window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ezt_contact_source: root.dataset.eztSource,
        ezt_product_id: root.dataset.eztProductId || '', ...extra });
    } catch (_) {}
  }
  let turnstileScript;
  function loadTurnstile() {
    if (window.turnstile?.render) return Promise.resolve(window.turnstile);
    if (turnstileScript) return turnstileScript;
    turnstileScript = new Promise((resolve, reject) => {
      let done = false;
      const finish = (error) => {
        if (done) return;
        done = true; clearInterval(poll); clearTimeout(timer);
        if (error) { turnstileScript = null; reject(error); } else resolve(window.turnstile);
      };
      const poll = setInterval(() => { if (window.turnstile?.render) finish(); }, 100);
      const timer = setTimeout(() => finish(new Error('The photo security check could not load. Check your connection or browser blocker, then retry.')), 20000);
      if (!document.querySelector('script[src*="challenges.cloudflare.com/turnstile/"]')) {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.onerror = () => { script.remove(); finish(new Error('The photo security check could not load. Please retry or email your photos.')); };
        document.head.append(script);
      }
    });
    return turnstileScript;
  }
  class SupportForm {
    constructor(root) {
      this.root = root;
      this.form = by(root, 'form');
      this.files = [];
      this.busy = false;
      this.started = false;
      this.run = 0;
      this.key = `ezt-support-v1:${location.pathname}:${this.form.id}`;
      this.submit = by(root, '[data-ezt-submit]');
      this.label = by(root, '[data-ezt-submit-label]');
      this.photoInput = by(root, '[data-ezt-files]');
      this.photoLinks = by(root, '[data-ezt-photo-links]');
      this.vehicle = by(root, '[data-ezt-vehicle]');
      this.message = by(root, '[data-ezt-message]');
      this.sent = !!by(root, '[data-ezt-sent]');
      this.endpoint = this.getEndpoint();
      const previous = storage.get(this.key);
      if (this.sent) {
        if (previous?.pending && Date.now() - previous.time < DRAFT_AGE) {
          metric('ezt_contact_success', root, { ezt_has_photos: !!previous.photos?.length });
        }
        storage.remove(this.key);
      } else {
        this.restore(previous);
        this.syncTopic();
        this.syncVehicle();
        all(this.root, '[data-ezt-trim-required]').forEach(control => this.validateTopicField(control));
        this.syncContext();
        this.bind();
        this.setupPhotos();
        if (window.EZTVehicle?.ready) window.EZTVehicle.ready.then(() => this.syncVehicle());
      }
      this.setupDialog();
    }
    getEndpoint() {
      try {
        const value = this.root.dataset.eztUploadUrl?.trim();
        if (!value || !this.root.dataset.eztSiteKey?.trim()) return '';
        const url = new URL(value);
        if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash) return '';
        // Guard against accidentally sending private support photos into the public Garage.
        if (/ezt-garage/i.test(url.hostname + url.pathname)) return '';
        return url.href.replace(/\/$/, '').replace(/\/upload$/, '');
      } catch (_) { return ''; }
    }
    bind() {
      this.form.addEventListener('input', (event) => {
        if (!this.started) { this.started = true; metric('ezt_contact_start', this.root); }
        if (event.target === this.vehicle) {
          this.vehicle.dataset.userEdited = 'true';
          by(this.root, '[data-ezt-saved-vehicle]').hidden = true;
        }
        this.validateTopicField(event.target);
        clearTimeout(this.saveTimer);
        this.saveTimer = setTimeout(() => this.save(), 220);
      });
      all(this.root, '[data-ezt-topic]').forEach(radio => radio.addEventListener('change', () => { this.syncTopic(); this.save(); }));
      this.form.addEventListener('submit', event => {
        all(this.root, '[data-ezt-trim-required]').forEach(control => this.validateTopicField(control));
        if (!this.form.checkValidity()) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.form.reportValidity();
          return;
        }
        if (this.busy || this.files.some(file => file.status !== 'ready')) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.error(this.busy ? 'Please wait for your photos to finish, or remove them before sending.' : 'A photo has not uploaded. Retry it or remove it before sending.');
          return;
        }
        if (by(this.root, '[data-ezt-honeypot]').value) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.error('Your message could not be sent. Please refresh and try again.');
          return;
        }
        this.syncContext(); this.syncLinks(); this.save(true);
        // Do not preventDefault or call form.submit here. Shopify owns CAPTCHA and delivery.
      }, true);
      window.addEventListener('pagehide', () => { if (!this.sent) this.save(this.pending); });
      const updateVehicle = () => {
        if (!this.root.isConnected) { window.removeEventListener('ezt:vehicle:changed', updateVehicle); return; }
        this.syncVehicle();
      };
      window.addEventListener('ezt:vehicle:changed', updateVehicle);
      all(this.root, '[data-ezt-email-photos], [data-ezt-direct-email]').forEach(link => link.addEventListener('click', () => this.emailFallback(link)));
      this.photoInput?.addEventListener('change', () => {
        const selected = Array.from(this.photoInput.files || []);
        this.photoInput.value = ''; this.addFiles(selected);
      });
      const drop = by(this.root, '[data-ezt-dropzone]');
      if (drop) {
        ['dragenter', 'dragover'].forEach(name => drop.addEventListener(name, event => {
          event.preventDefault(); if (!this.busy) drop.classList.add('is-dragover');
        }));
        ['dragleave', 'drop'].forEach(name => drop.addEventListener(name, event => { event.preventDefault(); drop.classList.remove('is-dragover'); }));
        drop.addEventListener('drop', event => this.addFiles(Array.from(event.dataTransfer?.files || [])));
      }
      by(this.root, '[data-ezt-retry]')?.addEventListener('click', () => this.upload());
      by(this.root, '[data-ezt-clear-photos]')?.addEventListener('click', () => this.clearPhotos());
    }
    setupPhotos() {
      const tools = by(this.root, '[data-ezt-upload-tools]');
      if (tools && this.endpoint && window.fetch && window.FormData && window.AbortController) {
        tools.hidden = false;
        by(this.root, '[data-ezt-photo-fallback]').hidden = true;
      }
      this.renderFiles();
    }
    syncTopic() {
      if (!by(this.root, '[data-ezt-topic-group]')) return;
      const topic = by(this.root, '[data-ezt-topic]:checked')?.value || 'Product technical question';
      all(this.root, '[data-ezt-topic-fields]').forEach(panel => {
        const active = panel.dataset.eztTopicFields === topic;
        panel.hidden = !active;
        if (panel.tagName === 'FIELDSET') panel.disabled = !active;
        all(panel, 'input, select, textarea').forEach(control => {
          control.disabled = !active;
          control.required = active && control.hasAttribute('data-ezt-required');
          this.validateTopicField(control);
        });
      });
      this.message.placeholder = topic === 'Order inquiry'
        ? 'Tell us what you need help with on your order.'
        : topic === 'Quote request'
          ? 'Tell us any details that will help us quote the right part or kit.'
          : 'Ask about fitment, installation or troubleshooting.';
    }
    validateTopicField(control) {
      if (!control?.hasAttribute?.('data-ezt-trim-required')) return;
      control.setCustomValidity(!control.disabled && control.required && control.value && !control.value.trim()
        ? 'Please enter a value, not just spaces.' : '');
    }
    syncVehicle() {
      if (!this.vehicle || this.vehicle.dataset.userEdited === 'true') return;
      const saved = window.EZTVehicle?.get?.();
      if (!saved?.year || !saved?.make || !saved?.model) {
        if (this.vehicle.dataset.fromFinder === 'true') this.vehicle.value = '';
        this.validateTopicField(this.vehicle);
        by(this.root, '[data-ezt-saved-vehicle]').hidden = true;
        return;
      }
      this.vehicle.value = [saved.year, saved.make, saved.model].join(' ').slice(0, 160);
      this.vehicle.dataset.fromFinder = 'true';
      this.validateTopicField(this.vehicle);
      by(this.root, '[data-ezt-saved-vehicle]').hidden = false;
    }
    syncContext() {
      by(this.root, '[data-ezt-page-url]').value = location.origin + location.pathname;
      const reference = by(this.root, '[data-ezt-reference]');
      if (!reference.value) reference.value = `EZT-${id()}`;
      if (this.root.dataset.eztSource !== 'product') {
        const handle = new URLSearchParams(location.search).get('product');
        if (handle && /^[a-zA-Z0-9-]{1,180}$/.test(handle)) {
          this.hidden('contact[Product handle]', handle);
          this.hidden('contact[Product URL]', `${location.origin}/products/${handle}`);
        }
        return;
      }
      let variants = [];
      try { variants = JSON.parse(by(this.root, '[data-ezt-variants]')?.textContent || '[]'); } catch (_) {}
      const section = this.root.closest('.shopify-section') || document;
      const cartForm = by(section, 'form[action*="/cart/add"]');
      const selected = cartForm?.querySelector('[name="id"]')?.value;
      const queryId = new URLSearchParams(location.search).get('variant');
      const variant = variants.find(value => String(value.id) === String(selected || queryId)) ||
        variants.find(value => String(value.id) === by(this.root, '[data-ezt-variant-id]').value);
      if (!variant) return;
      by(this.root, '[data-ezt-variant-id]').value = String(variant.id);
      by(this.root, '[data-ezt-variant-title]').value = variant.title || '';
      by(this.root, '[data-ezt-sku]').value = variant.sku || '';
      by(this.root, '[data-ezt-variant-display]').textContent = variant.title === 'Default Title' ? '' : variant.title;
      const url = new URL(by(this.root, '[data-ezt-product-url]').value);
      url.searchParams.set('variant', String(variant.id));
      by(this.root, '[data-ezt-product-url]').value = url.toString();
    }
    hidden(name, value) {
      let input = Array.from(this.form.elements).find(element => element.name === name);
      if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = name; this.form.append(input); }
      input.value = value;
    }
    restore(draft) {
      if (!draft || Date.now() - draft.time > DRAFT_AGE || draft.time > Date.now() + 60000) { storage.remove(this.key); return; }
      ['contact[name]', 'contact[email]', 'contact[Vehicle]', 'contact[body]', 'contact[Order number]', 'contact[Order inquiry]', 'contact[Brand]', 'contact[Requested item]', 'contact[Reference]'].forEach(name => {
        const element = this.form.elements.namedItem(name);
        if (element && typeof draft.values?.[name] === 'string') element.value = draft.values[name];
      });
      if (draft.vehicleEdited && this.vehicle) this.vehicle.dataset.userEdited = 'true';
      all(this.root, '[data-ezt-topic]').forEach(radio => { radio.checked = radio.value === draft.topic; });
      if (!by(this.root, '[data-ezt-topic]:checked')) { const first = by(this.root, '[data-ezt-topic]'); if (first) first.checked = true; }
      this.files = (Array.isArray(draft.photos) ? draft.photos : []).slice(0, MAX_FILES).filter(photo => this.validPhotoURL(photo.url)).map(photo => ({ id: id(), name: String(photo.name || 'Photo').slice(0,120), url: photo.url, status: 'ready' }));
      this.syncLinks();
    }
    save(pending = false) {
      if (this.sent) return;
      this.pending = pending;
      const values = {};
      ['contact[name]', 'contact[email]', 'contact[Vehicle]', 'contact[body]', 'contact[Order number]', 'contact[Order inquiry]', 'contact[Brand]', 'contact[Requested item]', 'contact[Reference]'].forEach(name => { values[name] = this.form.elements.namedItem(name)?.value || ''; });
      storage.set(this.key, { time: Date.now(), values, pending, vehicleEdited: this.vehicle?.dataset.userEdited === 'true',
        topic: by(this.root, '[data-ezt-topic]:checked')?.value || 'Product / fitment',
        photos: this.files.filter(photo => photo.status === 'ready').map(photo => ({ name: photo.name, url: photo.url })) });
    }
    error(text, focus = true) {
      const box = by(this.root, '[data-ezt-client-error]');
      box.textContent = text || ''; box.hidden = !text;
      if (text && focus) box.focus({ preventScroll: false });
    }
    status(text) { const el = by(this.root, '[data-ezt-upload-status]'); if (el) el.textContent = text; }
    validPhotoURL(value) {
      try { const url = new URL(value); return !!this.endpoint && url.origin === new URL(this.endpoint).origin && url.protocol === 'https:' && url.pathname.includes('/photo/') && !!url.searchParams.get('sig') && Number(url.searchParams.get('expires')) > Date.now() / 1000; } catch (_) { return false; }
    }
    addFiles(selected) {
      if (this.busy || !selected.length || !this.endpoint) return;
      if (this.files.length + selected.length > MAX_FILES) { this.error('Please choose up to 3 photos total. Remove a photo to add another.'); return; }
      const allowedMimes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
      for (const file of selected) {
        if ((!allowedMimes.includes(file.type.toLowerCase()) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) || !file.size) { this.error('Please choose a JPG, PNG, WebP or HEIC photo. PDFs, videos and SVG files are not supported.'); return; }
        if (file.size > MAX_BYTES) { this.error(`${file.name.slice(0,80)} is larger than 8 MB. Choose a smaller photo or screenshot.`); return; }
      }
      this.error('');
      selected.forEach(file => this.files.push({ id: id(), file, name: file.name, preview: URL.createObjectURL(file), status: 'pending', url: '' }));
      this.renderFiles(); this.upload();
    }
    renderFiles() {
      const list = by(this.root, '[data-ezt-photo-list]');
      if (!list) return;
      list.replaceChildren();
      this.files.forEach(photo => {
        const item = document.createElement('li'); item.className = `ezt-support-photo-item is-${photo.status}`;
        const fallback = document.createElement('span'); fallback.className = 'ezt-support-photo-item__file'; fallback.textContent = /\.hei[cf]$/i.test(photo.name) ? 'HEIC' : 'PHOTO';
        if (photo.preview) {
          const image = document.createElement('img'); image.src = photo.preview; image.alt = ''; image.onerror = () => image.replaceWith(fallback); item.append(image);
        } else item.append(fallback);
        const copy = document.createElement('span'); copy.className = 'ezt-support-photo-item__copy';
        const name = document.createElement('strong'); name.textContent = photo.name;
        const status = document.createElement('small');
        status.textContent = photo.status === 'ready' ? 'Uploaded. Ready to send with your message.' : photo.status === 'error' ? 'Not uploaded. Retry or remove.' : 'Preparing your photo...';
        copy.append(name, status);
        const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'ezt-support-photo-remove'; remove.textContent = '×'; remove.setAttribute('aria-label', `Remove ${photo.name}`); remove.disabled = this.busy;
        remove.addEventListener('click', () => {
          if (photo.preview) URL.revokeObjectURL(photo.preview);
          this.files = this.files.filter(value => value.id !== photo.id);
          this.syncLinks(); this.renderFiles(); this.save();
          if (!this.files.some(value => value.status !== 'ready')) { this.error(''); this.status(''); }
        });
        item.append(copy, remove); list.append(item);
      });
      if (this.photoInput) this.photoInput.disabled = this.busy || this.files.length >= MAX_FILES;
      this.submit.disabled = this.busy;
      this.label.textContent = this.busy ? 'Preparing photos...' : 'Send message';
      const actions = by(this.root, '[data-ezt-upload-actions]');
      if (actions) actions.hidden = !this.busy && !this.files.some(value => value.status === 'error');
      const retry = by(this.root, '[data-ezt-retry]'); if (retry) retry.hidden = this.busy;
    }
    syncLinks() {
      this.photoLinks.value = this.files.filter(file => file.status === 'ready').map((photo, index) => `Photo ${index + 1}: ${photo.url}`).join('\n');
    }
    async securityToken(run) {
      const turnstile = await loadTurnstile();
      if (run !== this.run) throw new Error('Upload canceled.');
      const target = by(this.root, '[data-ezt-turnstile]'); target.hidden = false;
      this.status('Checking your photo upload. This usually takes a moment.');
      return new Promise((resolve, reject) => {
        let done = false;
        const finish = (error, token) => {
          if (done) return; done = true; clearTimeout(timer); this.cancelToken = null;
          if (this.widget !== undefined) { try { turnstile.remove(this.widget); } catch (_) {} this.widget = undefined; }
          target.hidden = true;
          error ? reject(error) : resolve(token);
        };
        const timer = setTimeout(() => finish(new Error('The photo security check timed out. Please retry or email your photos.')), 90000);
        this.cancelToken = () => finish(new Error('Upload canceled.'));
        try {
          this.widget = turnstile.render(target, {
            sitekey: this.root.dataset.eztSiteKey, action: 'ezt_support_upload', theme: 'light', size: 'flexible', appearance: 'interaction-only',
            callback: token => finish(null, token),
            'error-callback': () => { finish(new Error('The photo security check did not finish. Please retry or email your photos.')); return true; },
            'expired-callback': () => finish(new Error('The photo security check expired. Please retry.')),
            'timeout-callback': () => finish(new Error('The photo security check timed out. Please retry.'))
          });
        } catch (_) { finish(new Error('Photo verification is unavailable. Please retry or email your photos.')); }
      });
    }
    async upload() {
      if (this.busy || !this.endpoint) return;
      const batch = this.files.filter(file => file.status !== 'ready' && file.file);
      if (!batch.length) return;
      const run = ++this.run;
      this.busy = true; this.error(''); batch.forEach(file => { file.status = 'pending'; }); this.renderFiles();
      let timer;
      try {
        const token = await this.securityToken(run);
        if (run !== this.run) return;
        this.status(`Uploading ${batch.length === 1 ? 'your photo' : `your ${batch.length} photos`}...`);
        const data = new FormData(); batch.forEach(file => data.append('photos', file.file, file.name));
        data.append('website', by(this.root, '[data-ezt-honeypot]').value);
        this.abort = new AbortController();
        timer = setTimeout(() => this.abort?.abort(), 60000);
        const response = await fetch(`${this.endpoint}/upload`, { method: 'POST', headers: { 'X-Turnstile-Token': token }, body: data, signal: this.abort.signal, credentials: 'omit', referrerPolicy: 'no-referrer' });
        let result; try { result = await response.json(); } catch (_) { throw new Error('The photo service returned an unexpected response. Retry or email your photos.'); }
        if (!response.ok || !result.ok) throw new Error(typeof result.error === 'string' ? result.error : 'Your photos could not upload. Please retry.');
        if (!Array.isArray(result.files) || result.files.length !== batch.length || result.files.some(file => !this.validPhotoURL(file.url))) throw new Error('The photo service did not return valid photo links. Please retry or email your photos.');
        if (run !== this.run) return;
        batch.forEach((file, index) => { file.status = 'ready'; file.url = result.files[index].url; });
        this.syncLinks(); this.save(); this.status('Photos ready. Send your message when you are ready.');
      } catch (error) {
        if (run !== this.run) return;
        batch.forEach(file => { file.status = 'error'; });
        this.status('Your message has not been sent.');
        this.error(error.name === 'AbortError' ? 'The photo upload timed out. Retry, or remove your photos and send the message without them.' : `${error.message} You can also remove your photos and send without them.`, false);
        by(this.root, '[data-ezt-photo-fallback]').hidden = false;
        metric('ezt_contact_upload_error', this.root);
      } finally {
        clearTimeout(timer);
        if (run === this.run) { this.busy = false; this.abort = null; this.renderFiles(); }
      }
    }
    clearPhotos() {
      ++this.run; this.abort?.abort(); this.cancelToken?.();
      this.files.forEach(file => { if (file.preview) URL.revokeObjectURL(file.preview); });
      this.files = []; this.busy = false; this.syncLinks(); this.renderFiles(); this.save(); this.error(''); this.status('Photos removed. You can send your message now.');
    }
    emailFallback(link) {
      this.syncContext();
      const topic = by(this.root, '[data-ezt-topic]:checked')?.value;
      const title = by(this.root, '[data-ezt-product-title]')?.value || (topic ? `${topic} for EZT` : 'Question for EZT');
      const details = ['Order number', 'Order inquiry', 'Brand', 'Requested item', 'Vehicle'].map(label => {
        const field = this.form.elements.namedItem(`contact[${label}]`);
        return field && !field.matches(':disabled') && field.value.trim() ? `${label}: ${field.value.trim()}` : '';
      }).filter(Boolean);
      const name = this.form.elements.namedItem('contact[name]')?.value.trim();
      const context = [name ? `Name: ${name}` : '', topic ? `Topic: ${topic}` : '', ...details,
        by(this.root, '[data-ezt-product-url]')?.value || '', link.hasAttribute('data-ezt-email-photos') ? 'Please attach your photos before sending.' : ''].filter(Boolean).join('\n\n');
      const body = [context, this.message?.value.slice(0, Math.max(0, 1798 - context.length))].filter(Boolean).join('\n\n');
      link.href = `mailto:${this.root.dataset.eztSupportEmail}?subject=${encodeURIComponent(title.slice(0,180))}&body=${encodeURIComponent(body)}`;
    }
    setupDialog() {
      const dialog = this.root.closest('[data-ezt-dialog]');
      if (!dialog) { if (this.sent) by(this.root, '[data-ezt-sent]').focus({ preventScroll: true }); return; }
      const trigger = all(document, '[data-ezt-question-open]').find(button => button.dataset.eztQuestionOpen === dialog.id);
      if (!trigger || typeof dialog.showModal !== 'function') return;
      const open = () => {
        if (!this.sent) { this.syncContext(); this.syncVehicle(); }
        if (dialog.open) return;
        this.previousOverflow = document.body.style.overflow;
        dialog.showModal(); document.body.style.overflow = 'hidden';
        trigger.setAttribute('aria-expanded', 'true');
        metric('ezt_contact_open', this.root);
        requestAnimationFrame(() => {
          const status = by(this.root, '[data-ezt-sent], [data-ezt-server-error]');
          if (status) status.focus({ preventScroll: true });
          else if (matchMedia('(min-width: 781px)').matches) this.form.elements.namedItem('contact[email]')?.focus({ preventScroll: true });
          else by(dialog, '[data-ezt-question-close]').focus({ preventScroll: true });
        });
      };
      trigger.addEventListener('click', event => { event.preventDefault(); open(); });
      by(dialog, '[data-ezt-question-close]').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
      });
      dialog.addEventListener('close', () => {
        document.body.style.overflow = this.previousOverflow || '';
        trigger.setAttribute('aria-expanded', 'false'); trigger.focus({ preventScroll: true });
      });
      if (this.sent || by(this.root, '[data-ezt-server-error]')) open();
    }
  }
  function init() {
    all(document, '[data-ezt-support]').forEach(root => {
      if (instances.has(root)) return;
      try { const instance = new SupportForm(root); instances.set(root, instance); }
      catch (error) { console.error('EZT contact initialization failed. The standard email form is still available.', error); }
    });
  }
  window.EZTSupport = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
  window.addEventListener('pageshow', event => { if (event.persisted) init(); });
})();
